/**
 * React hook for integrating slash commands into AI chat
 */

import { useState, useCallback } from 'react';
import { Component, ComponentInput } from '@/types';
import { slashCommandSystem } from './SlashCommandSystem';
import { useEditorStore } from '@/stores/editor/editor-store';
import { responsivePositionTranslator } from '@/services/responsive/ResponsivePositionTranslator';
import type { ComponentUpdatePayload } from '@/types/component-update';
import type { ComponentDefaultSourceContext } from './utils/component-defaults';

export type SlashCommandPlannedMutation =
  | { type: 'addComponent'; component: ComponentInput }
  | { type: 'addComponents'; components: ComponentInput[]; containerId?: string }
  | { type: 'deleteComponent'; componentId: string; targetComponent?: Component }
  | {
      type: 'updateComponent';
      componentId: string;
      changes: Partial<ComponentUpdatePayload>;
      targetComponent?: Component;
    };

export interface SlashCommandResult {
  success: boolean;
  message: string;
  propertyChanges?: Record<string, unknown>;
  plannedMutations?: SlashCommandPlannedMutation[];
  applyMutations?: () => void;
  shouldContinue?: boolean;
}

export interface SlashCommandExecutionOptions {
  sourceContext?: ComponentDefaultSourceContext;
}

function toComponentInput(
  component: Partial<Component>,
  projectId: string,
  pageId: string,
): ComponentInput | null {
  if (!component.type) {
    return null;
  }

  return {
    id: component.id,
    projectId,
    pageId,
    name: component.name ?? `${component.type}-${Date.now()}`,
    type: component.type,
    properties: component.properties ?? {},
    position: component.position,
    size: component.size ?? { width: 200, height: 100 },
    style: component.style ?? {},
    responsivePosition:
      component.responsivePosition ??
      responsivePositionTranslator.generateDefaultResponsivePosition(component.type),
    parentId: component.parentId,
    children: component.children ?? [],
    _childComponents: component._childComponents,
    order: component.order ?? 0,
    zIndex: component.zIndex,
    locked: component.locked ?? false,
    hidden: component.hidden ?? false,
    version: component.version ?? 1,
    createdBy: component.createdBy ?? '',
    lastModifiedBy: component.lastModifiedBy ?? '',
    lastModifiedAt: component.lastModifiedAt ?? new Date(),
    isDeleted: component.isDeleted ?? false,
    deletedAt: component.deletedAt,
    isSubComponent: component.isSubComponent,
    subComponentType: component.subComponentType,
    interactions: component.interactions,
    metadata: component.metadata,
  };
}

export const useSlashCommands = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const {
    addComponent,
    addMultipleComponents,
    deleteComponent,
    updateComponent,
    project,
    currentPage,
    currentPageId,
    clearPendingContainer,
  } = useEditorStore();

  /**
   * Check if input is a slash command
   */
  const isSlashCommand = useCallback((input: string): boolean => {
    return slashCommandSystem.isSlashCommand(input);
  }, []);

  /**
   * Execute a slash command
   */
  const executeSlashCommand = useCallback(async (
    command: string,
    targetComponent?: Component | null,
    isCanvas?: boolean,
    options?: SlashCommandExecutionOptions,
  ): Promise<SlashCommandResult> => {
    setIsProcessing(true);

    try {
      const result = await slashCommandSystem.execute(command, targetComponent || undefined, {
        activeProject: project ?? undefined,
        activePageId: currentPage?.id ?? currentPageId,
        isCanvas,
        sourceContext: options?.sourceContext,
      });

      let message = "";
      let propertyChanges: Record<string, unknown> | undefined;
      const plannedMutations: SlashCommandPlannedMutation[] = [];
      let shouldContinue = true;
      const activeProjectId = project?.id;
      const activePageId = currentPage?.id ?? currentPageId;

      if (result.success) {
        switch (result.action) {
          case 'modify':
            // Apply property or style changes
            if (result.changes) {
              // Preserve the structure of properties and style
              propertyChanges = {
                ...(result.changes.properties ? { properties: result.changes.properties } : {}),
                ...(result.changes.style ? { style: result.changes.style } : {}),
                ...(result.changes.size ? {
                  width: result.changes.size.width,
                  height: result.changes.size.height
                } : {})
              };
              message = result.message || `✅ Applied ${command}`;
            }
            if (result.additionalUpdates) {
              plannedMutations.push(
                ...result.additionalUpdates.map((update) => ({
                  type: 'updateComponent' as const,
                  componentId: update.componentId,
                  changes: update.changes,
                  targetComponent: update.targetComponent,
                })),
              );
            }
            break;

          case 'add':
          case 'template':
            if (!activeProjectId || !activePageId) {
              message = '❌ Active project and page context are required to add components';
              shouldContinue = false;
              break;
            }

            // Add new component(s)
            if (result.component) {
              const newComponent = toComponentInput(result.component, activeProjectId, activePageId);
              if (newComponent) {
                plannedMutations.push({ type: 'addComponent', component: newComponent });
                message = result.message || `✅ Added ${newComponent.type} component`;
              } else {
                message = '❌ Slash command returned an invalid component payload';
                shouldContinue = false;
              }
            } else if (result.components) {
              // Add multiple components (template)
              const components = result.components
                .map((component) => toComponentInput(component, activeProjectId, activePageId))
                .filter((component): component is ComponentInput => component !== null);

              if (components.length > 0) {
                plannedMutations.push({
                  type: 'addComponents',
                  components,
                  containerId: result.containerId,
                });
                message = result.message || '✅ Template added successfully';
              } else {
                message = '❌ Slash command returned no valid components to add';
                shouldContinue = false;
              }
            }
            break;

          case 'delete':
            // Delete component
            if (targetComponent) {
              plannedMutations.push({
                type: 'deleteComponent',
                componentId: targetComponent.id,
                targetComponent,
              });
              message = '✅ Component deleted';
            } else {
              message = '❌ No component selected to delete';
            }
            break;

          case 'help':
            // Show help message
            message = result.message || '';
            shouldContinue = false; // Don't continue to AI for help commands
            break;

          default:
            message = result.message || '✅ Command executed';
        }
      } else {
        message = `❌ ${result.error}`;
        if (result.suggestions && result.suggestions.length > 0) {
          message += `\n\nDid you mean:\n${result.suggestions.join('\n')}`;
        }
        shouldContinue = false; // Don't continue to AI for errors
      }

      return {
        success: result.success,
        message,
        propertyChanges,
        plannedMutations,
        applyMutations: () => {
          for (const mutation of plannedMutations) {
            switch (mutation.type) {
              case 'addComponent':
                addComponent(mutation.component);
                break;
              case 'addComponents':
                addMultipleComponents(mutation.components);
                if (mutation.containerId) {
                  clearPendingContainer(mutation.containerId);
                  console.log('🧹 [PENDING_CONTAINERS] Cleared pending container:', mutation.containerId);
                }
                break;
              case 'deleteComponent':
                deleteComponent(mutation.componentId);
                break;
              case 'updateComponent':
                updateComponent(mutation.componentId, mutation.changes);
                break;
              default:
                break;
            }
          }
        },
        shouldContinue
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error('Slash command error:', error);
      return {
        success: false,
        message: `❌ Error executing command: ${message}`,
        shouldContinue: false
      };
    } finally {
      setIsProcessing(false);
    }
  }, [project, currentPage, currentPageId, addComponent, addMultipleComponents, deleteComponent, updateComponent, clearPendingContainer]);

  /**
   * Get command suggestions
   */
  const getSuggestions = useCallback((partial: string): string[] => {
    if (!partial.startsWith('/')) return [];
    const commandPart = partial.substring(1);
    return slashCommandSystem.getSuggestions(commandPart);
  }, []);

  /**
   * Get all available commands
   */
  const getAllCommands = useCallback(() => {
    return slashCommandSystem.getAllCommands();
  }, []);

  return {
    isSlashCommand,
    executeSlashCommand,
    getSuggestions,
    getAllCommands,
    isProcessing
  };
};
